import { useLocalStorage } from '@uidotdev/usehooks';
import { GLOBAL_SEARCH_ENTITY_ICON as ICON } from 'components/global-search/entityDisplay';
import { SchoolOutlined, SquareCheckIcon, type SvgIconComponent } from 'components/icons';
import { useCallback, useMemo } from 'react';
import { APIUserSlice } from 'store/api/user';
import { isTourMode, tourSample, useTourMode } from 'tour/tourMode';
import type { GlobalSearchEntityType } from 'types/globalSearch';
import type { ListKey } from 'types/listViews';
import { RECORD_KIND_FAMILY } from 'utils/families';
import { pathForResult } from 'utils/globalSearchRoutes';

/**
 * Something pinned to the new design's sidebar: a record, or a saved list view. A view's `url` is the page it
 * opens on, without its query, which the sidebar reads from the view itself so a saved change shows.
 */
export type Pin = {
    /** `matter:12`, `contact:7`, `team:3`, `view:41`. */
    key: string;
    label: string;
    url: string;
    view?: { listKey: ListKey; id: number };
};

/**
 * Every record that pins, by its page's first URL segment, which is also its pins' key prefix: `matter:12` opens
 * `/matter/12`. Its glyph is the one search draws it with; its family is `RECORD_KIND_FAMILY`'s.
 */
export const PIN_KINDS = {
    matter: { icon: ICON.MATTER },
    projects: { icon: ICON.PROJECT },
    delegations: { icon: ICON.DELEGATION },
    correspondence: { icon: ICON.CORRESPONDENCE },
    contact: { icon: ICON.PARTY },
    group: { icon: ICON.PARTY_GROUP },
    contract: { icon: ICON.CONTRACT },
    effort: { icon: ICON.EFFORT },
    'experience-records': { icon: ICON.EXPERIENCE_RECORD },
    debriefing: { icon: ICON.DEBRIEFING },
    employee: { icon: ICON.EMPLOYEE },
    team: { icon: ICON.TEAM },
    office: { icon: ICON.OFFICE },
    'practice-area': { icon: ICON.PRACTICE_AREA },
    trainings: { icon: SchoolOutlined },
    invoice: { icon: ICON.INVOICE },
    expense: { icon: ICON.EXPENSE },
    'bank-transfers': { icon: ICON.BANK_TRANSACTION },
    task: { icon: SquareCheckIcon },
    timecards: { icon: ICON.TIMECARD },
    'job-role': { icon: ICON.JOB_ROLE },
    'job-position': { icon: ICON.JOB_POSITION },
} satisfies Record<string, { icon: SvgIconComponent }>;

export type PinKind = keyof typeof PIN_KINDS;

const isPinKind = (kind: string): kind is PinKind => Object.hasOwn(PIN_KINDS, kind);

/** A record's pin, as its header's star and the search dialog store it. */
export const recordPin = (kind: PinKind, id: number, label: string): Pin => ({
    key: `${kind}:${id}`,
    label,
    url: `/${kind}/${id}`,
});

/**
 * A same-origin link to a record page, `/<kind>/<id>` with digits for the id (a further segment or a query is
 * ignored), as that record's pin: under the link's title, else its text with whitespace collapsed, at most 120
 * characters. None for a link to anything else, or with neither title nor text.
 */
export function linkPin(
    link: Pick<HTMLAnchorElement, 'href' | 'title' | 'textContent'>,
    origin = window.location.origin
): Pin | undefined {
    const url = new URL(link.href, origin);
    const [, kind = '', id] = /^\/([^/]+)\/(\d+)(?:\/|$)/.exec(url.pathname) ?? [];
    const label = (link.title.trim() || (link.textContent ?? '').replace(/\s+/g, ' ').trim()).slice(0, 120);
    return url.origin === origin && isPinKind(kind) && label ? recordPin(kind, Number(id), label) : undefined;
}

/** A search result's pin, keyed as its record header keys it; none for a family with no record page of its own. */
export function searchResultPin(entityType: GlobalSearchEntityType, id: number, label: string): Pin | undefined {
    const kind = pathForResult(entityType, id).split('/')[1];
    return isPinKind(kind) ? recordPin(kind, id, label) : undefined;
}

/** How the sidebar marks a record pin, its glyph and its family, read off its key; nothing for a view's. */
export function pinMark(pin: Pin) {
    const kind = pin.key.split(':')[0];
    return isPinKind(kind) ? { ...PIN_KINDS[kind], family: RECORD_KIND_FAMILY[kind] } : undefined;
}

const isPin = (value: unknown): value is Pin => {
    const { key, label, url } = (value ?? {}) as Partial<Pin>;
    return typeof key === 'string' && typeof label === 'string' && typeof url === 'string';
};

/** The stored pins but the one with `key`. */
const pinsOtherThan = (stored: unknown, key: string) =>
    (Array.isArray(stored) ? stored.filter(isPin) : []).filter((pin) => pin.key !== key);

/**
 * The signed-in employee's pins, kept in this browser under a key carrying their tenant and their id, so two people
 * sharing a browser never see each other's: an employee id repeats from one tenant to the next. Read from the cached
 * current user: the layout loads it before any page. The guided tour shows its own and changes none.
 */
export function usePins() {
    const { owner } = APIUserSlice.endpoints.getUserData.useQueryState(undefined, {
        selectFromResult: ({ data }) => ({
            owner: data?.tenants?.[0] ? `${data.tenants[0]}.${data.id}` : undefined,
        }),
    });
    // No initial value, which the hook would write: a reader alone leaves the browser's storage as it is.
    const [stored, setStored] = useLocalStorage<unknown>(`crespect.pins.${owner ?? 'none'}`, undefined);
    const touring = useTourMode();
    const pins = useMemo(
        () => (touring ? (tourSample()?.pins ?? []) : Array.isArray(stored) ? stored.filter(isPin) : []),
        [stored, touring]
    );

    const isPinned = useCallback((key: string) => pins.some((pin) => pin.key === key), [pins]);

    /** Keeps the pin, last, or drops the one with its key. Idempotent. */
    const setPinned = useCallback(
        (pin: Pin, pinned: boolean) => {
            if (owner === undefined || isTourMode()) return;
            setStored((previous: unknown) => {
                const others = pinsOtherThan(previous, pin.key);
                return pinned ? [...others, pin] : others;
            });
        },
        [owner, setStored]
    );

    /** Puts the pin at `index` among the others, clamped, whether it was pinned or not: a drop, a move, an Undo. */
    const placePin = useCallback(
        (pin: Pin, index: number) => {
            if (owner === undefined || isTourMode()) return;
            setStored((previous: unknown) => {
                const others = pinsOtherThan(previous, pin.key);
                const at = Math.min(Math.max(index, 0), others.length);
                return [...others.slice(0, at), pin, ...others.slice(at)];
            });
        },
        [owner, setStored]
    );

    const togglePin = useCallback((pin: Pin) => setPinned(pin, !isPinned(pin.key)), [setPinned, isPinned]);

    return { pins, isPinned, togglePin, setPinned, placePin };
}
